import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { selectUserRoles } from '../../features/auth/authSlice';
import { useGetAdminDashboardQuery } from '../../features/dashboard/dashboardApi';
import Card, { CardHeader } from '../../components/ui/Card';
import StatStrip, { StatStripItem } from '../../components/ui/StatStrip';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import CountUpStat from '../../components/ui/CountUpStat';
import { SkeletonCardGrid, SkeletonDetailCard } from '../../components/ui/Skeleton';
import { formatCurrency } from '../../lib/formatPlan';
import { formatRelativeTime } from '../../lib/relativeTime';
import { authErrorMessage } from '../../lib/authErrorMessage';

// Hex twins of the design system's status tones (tailwind.config.js) —
// recharts needs raw color values, it can't read Tailwind classes.
const STATUS_COLORS = {
  active: '#5B8CB5',
  pending_funding: '#F59E0B',
  pending: '#F59E0B',
  matured: '#8B8FA3',
  closed: '#6B7180',
  cancelled: '#EF4444',
  canceled: '#EF4444',
  rejected: '#EF4444',
  failed: '#EF4444',
};
const FALLBACK_STATUS_COLORS = ['#0EA5E9', '#8B5CF6', '#F97316', '#14B8A6'];

const CHART_TOOLTIP_STYLE = {
  background: '#1A2333',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 12,
  fontSize: 12,
  color: '#F3F4F7',
  boxShadow: '0 12px 28px rgba(0, 0, 0, 0.4)',
};

function formatStatusLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Compact axis/tick currency — full formatCurrency() is used everywhere
// values are read precisely (tooltips, stat cards); this is only for chart
// axis labels where "₦1.2M" reads better than the full figure.
function formatCompactCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '₦0';
  const abs = Math.abs(num);
  if (abs >= 1e9) return `₦${(num / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `₦${(num / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `₦${(num / 1e3).toFixed(1)}K`;
  return `₦${num.toLocaleString()}`;
}

/**
 * Donut chart + legend for portfolio.by_status — keys are dynamic
 * (whatever the API actually sends), never a hardcoded status list. Colors
 * map to the design system's status tones where the key is recognized,
 * and cycle through a small fallback palette for anything unexpected.
 */
function InvestmentsByStatusChart({ byStatus }) {
  const entries = Object.entries(byStatus)
    .map(([key, value], i) => ({
      key,
      name: formatStatusLabel(key),
      value: Number(value?.count ?? 0),
      color: STATUS_COLORS[key] || FALLBACK_STATUS_COLORS[i % FALLBACK_STATUS_COLORS.length],
    }))
    .filter((e) => e.value > 0);

  if (entries.length === 0) {
    return <p className="text-sm text-foreground-muted py-4 text-center">No investment data yet.</p>;
  }

  const total = entries.reduce((sum, e) => sum + e.value, 0);

  return (
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <div className="relative h-48 w-48 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={entries}
              dataKey="value"
              nameKey="name"
              innerRadius="64%"
              outerRadius="100%"
              paddingAngle={entries.length > 1 ? 3 : 0}
              strokeWidth={0}
              isAnimationActive
            >
              {entries.map((e) => (
                <Cell key={e.key} fill={e.color} />
              ))}
            </Pie>
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(value, name) => [value, name]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-foreground tabular-nums">{total}</span>
          <span className="text-xs text-foreground-subtle">Total</span>
        </div>
      </div>

      <div className="w-full flex-1 space-y-2.5">
        {entries.map((e) => (
          <div key={e.key} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-foreground-muted">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: e.color }} />
              {e.name}
            </span>
            <span className="font-medium text-foreground tabular-nums">{e.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Grouped bar chart, Received vs. Payouts across the two windows the API
 * actually gives us (this-month, YTD) — no invented daily/weekly trend
 * line, since the dashboard endpoint has no time-series data to back one.
 */
function CashFlowChart({ dashboard }) {
  const data = [
    { period: 'This month', Received: Number(dashboard.paymentsReceivedMonth) || 0, Payouts: Number(dashboard.payoutsMonth) || 0 },
    { period: 'YTD', Received: Number(dashboard.paymentsReceivedYtd) || 0, Payouts: Number(dashboard.payoutsYtd) || 0 },
  ];

  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barGap={8} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.08)" vertical={false} />
          <XAxis dataKey="period" tick={{ fill: '#9AA0AE', fontSize: 12 }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={formatCompactCurrency} tick={{ fill: '#9AA0AE', fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
          <Tooltip
            contentStyle={CHART_TOOLTIP_STYLE}
            cursor={{ fill: 'rgba(255, 255, 255, 0.04)' }}
            formatter={(value) => formatCurrency(value)}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
          <Bar dataKey="Received" fill="#5B8CB5" radius={[6, 6, 0, 0]} maxBarSize={44} />
          <Bar dataKey="Payouts" fill="#8B8FA3" radius={[6, 6, 0, 0]} maxBarSize={44} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CashFlowCard({ dashboard }) {
  return (
    <Card>
      <CardHeader title="Cash flow" />
      <CashFlowChart dashboard={dashboard} />
      <div className="grid grid-cols-2 gap-6 mt-6 pt-6 border-t border-border">
        <div className="space-y-3">
          <div>
            <p className="text-xs text-foreground-subtle">Payments received (mo.)</p>
            <p className="text-lg font-semibold text-foreground mt-0.5">{formatCurrency(dashboard.paymentsReceivedMonth)}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-subtle">Payments received (YTD)</p>
            <p className="text-lg font-semibold text-accent mt-0.5">{formatCurrency(dashboard.paymentsReceivedYtd)}</p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-foreground-subtle">Payouts (mo.)</p>
            <p className="text-lg font-semibold text-foreground mt-0.5">{formatCurrency(dashboard.payoutsMonth)}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-subtle">Payouts (YTD)</p>
            <p className="text-lg font-semibold text-foreground mt-0.5">{formatCurrency(dashboard.payoutsYtd)}</p>
          </div>
        </div>
      </div>
    </Card>
  );
}

function RecentActivityFeed({ dashboard }) {
  const merged = [...dashboard.recentTickets, ...dashboard.recentKyc]
    .filter((a) => a.createdAt)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  if (merged.length === 0) {
    return <p className="py-6 text-center text-foreground-muted">No recent activity.</p>;
  }

  return (
    <ul className="space-y-3">
      {merged.map((a, i) => (
        <li key={i} className="text-sm text-foreground-muted">
          {a.kind === 'ticket' ? (
            <>
              <span className="text-foreground">New ticket from {a.investor}</span> — {a.category || 'general'} ·{' '}
              {formatRelativeTime(a.createdAt)}
            </>
          ) : (
            <>
              <span className="text-foreground">KYC submitted by {a.investor}</span> · {formatRelativeTime(a.createdAt)}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function AdminDashboardContent() {
  const { data: dashboard, isLoading, isError, error } = useGetAdminDashboardQuery();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <SkeletonCardGrid count={4} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" />
        <SkeletonDetailCard fields={4} />
      </div>
    );
  }

  if (isError) {
    return (
      <Card>
        <p className="py-8 text-center text-danger">
          {authErrorMessage(error, {
            forbidden: "Your admin account doesn't have the view-dashboard permission needed to see this overview.",
            fallback: 'Could not load the dashboard.',
          })}
        </p>
      </Card>
    );
  }

  if (!dashboard) return null;

  return (
    <div className="space-y-6">
      <StatStrip columns={4}>
        <StatStripItem>
          <p className="text-foreground-subtle text-sm">Active AUM</p>
          <CountUpStat
            value={Number(dashboard.activeAum) || 0}
            formatter={(n) => formatCurrency(n)}
            className="text-2xl font-bold text-foreground mt-2 tabular-nums"
          />
        </StatStripItem>
        <StatStripItem>
          <p className="text-foreground-subtle text-sm">Total investors</p>
          <CountUpStat
            value={dashboard.investorsTotal}
            formatter={(n) => Math.round(n).toLocaleString()}
            className="text-2xl font-bold text-foreground mt-2 tabular-nums"
          />
        </StatStripItem>
        <StatStripItem>
          <p className="text-foreground-subtle text-sm">Pending KYC</p>
          <CountUpStat
            value={dashboard.pendingKyc}
            formatter={(n) => Math.round(n).toLocaleString()}
            className="text-2xl font-bold text-warning mt-2 tabular-nums"
          />
        </StatStripItem>
        <StatStripItem>
          <p className="text-foreground-subtle text-sm">Open tickets</p>
          <CountUpStat
            value={dashboard.activeSupportTickets}
            formatter={(n) => Math.round(n).toLocaleString()}
            className="text-2xl font-bold text-foreground mt-2 tabular-nums"
          />
        </StatStripItem>
      </StatStrip>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Investments by status" />
          <InvestmentsByStatusChart byStatus={dashboard.byStatus} />
        </Card>
        <CashFlowCard dashboard={dashboard} />
      </div>

      <Card>
        <CardHeader title="Upcoming maturities" subtitle="Next 30 days" />
        {dashboard.upcomingMaturities.length === 0 ? (
          <p className="py-6 text-center text-foreground-muted">No upcoming maturities.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-foreground-subtle border-b border-border">
                  <th className="py-2 pr-4 font-medium">Investor</th>
                  <th className="py-2 pr-4 font-medium">Plan</th>
                  <th className="py-2 pr-4 font-medium">Principal</th>
                  <th className="py-2 font-medium">Maturity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {dashboard.upcomingMaturities.map((m, i) => (
                  <tr key={i}>
                    <td className="py-3 pr-4 text-foreground">{m.investor}</td>
                    <td className="py-3 pr-4 text-foreground-muted">{m.plan}</td>
                    <td className="py-3 pr-4 text-foreground-muted">{formatCurrency(m.principal)}</td>
                    <td className="py-3 text-foreground-muted">{formatDate(m.maturityDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title="Recent activity" />
        <RecentActivityFeed dashboard={dashboard} />
      </Card>
    </div>
  );
}

function QuickLinkIcon({ d }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
      <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Everything below is live on the real API as of v1.14.0 — Investment
// Plans (v1.6.0), KYC + Onboard Client (v1.8.0), Investors/Investments
// (v1.12.0), Support Tickets/Reports (v1.14.0). Portfolios/Transactions
// remain "not yet populated," so there's still no standalone entry point
// for those — same reasoning as before for not inventing one.
const quickLinks = [
  { to: '/admin/admins', label: 'Manage administrators', superAdminOnly: true, d: 'M17 20v-1a4 4 0 00-4-4H7a4 4 0 00-4 4v1M9 11a4 4 0 100-8 4 4 0 000 8zM23 20v-1a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75' },
  { to: '/admin/investors', label: 'Manage investors', d: 'M17 20v-1a4 4 0 00-4-4H7a4 4 0 00-4 4v1M9 11a4 4 0 100-8 4 4 0 000 8zM23 20v-1a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75' },
  { to: '/admin/investments', label: 'All investments', d: 'M3 3v18h18M8 17V9M13 17V5M18 17v-7' },
  { to: '/admin/unlock-account', label: 'Unlock account', d: 'M17 11V7a5 5 0 00-10 0v4M5 11h14v9H5v-9z' },
  { to: '/admin/plans', label: 'Manage investment plans', d: 'M9 5h6M9 5a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6M9 16h6' },
  { to: '/admin/kyc', label: 'Review KYC queue', d: 'M12 2l8 4v6c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10V6l8-4zM9.5 12l1.8 1.8L15 10' },
  { to: '/admin/onboard-client', label: 'Onboard client', d: 'M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM20 8v6M17 11h6' },
  { to: '/admin/tickets', label: 'Support tickets', d: 'M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z' },
  { to: '/admin/reports', label: 'Reports', d: 'M3 3v18h18M8 17V9M13 17V5M18 17v-7' },
  { to: '/admin/audit-logs', label: 'View audit logs', superAdminOnly: true, d: 'M12 8v4l3 3M12 22a10 10 0 100-20 10 10 0 000 20z' },
];

export default function Overview() {
  const roles = useSelector(selectUserRoles);
  const isSuperAdmin = roles.includes('super-admin');
  const visibleLinks = quickLinks.filter((q) => !q.superAdminOnly || isSuperAdmin);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Admin dashboard</h1>
          <p className="text-foreground-muted text-sm mt-1">Platform-wide overview.</p>
        </div>
        <Link to="/admin/reports">
          <Button variant="secondary">Export report</Button>
        </Link>
      </div>

      <AdminDashboardContent />

      <Card>
        <CardHeader title="Quick links" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {visibleLinks.map((q) => (
            <Link
              key={q.label}
              to={q.to}
              className="relative flex items-center gap-3 rounded-xl border border-border bg-background-elevated px-3 py-3 text-sm font-medium text-foreground hover:border-accent/40 hover:bg-accent-soft/30 transition-colors"
            >
              <span className="shrink-0 h-9 w-9 rounded-lg bg-accent-soft text-accent flex items-center justify-center">
                <QuickLinkIcon d={q.d} />
              </span>
              <span className="leading-tight">{q.label}</span>
              {q.soon && (
                <Badge tone="warning" className="absolute top-1.5 right-1.5 !px-1.5 !py-0.5 text-[10px]">
                  Soon
                </Badge>
              )}
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
